import { memo, useMemo } from 'react';
import {
  FlexBox,
  StyledBox,
  StyledTypography,
  SubText,
} from './SensorAnalysis.styles';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { Box } from '@mui/material';
import { PNG } from 'components/Asset/PNG';
import { getImageName } from 'utils/helpers/mapAssetImage';
import { IgnitionStatusIndicator } from 'components/StatusIndicator';
import {
  addLiveDataToAssets,
  assetTypeMappings,
  getAssetTypeLabelKey,
} from 'views/FleetOverview/helpers/asset.helpers';
import { useTranslation } from 'react-i18next';

export const AssetDetails = () => {
  const { t } = useTranslation();
  const selectedAsset = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedAsset
  );
  const hierarchyInfo = useAppSelector((state) => state.hierarchyReducer);
  const { enableDarkTheme } = useAppTheme();

  const latestTagData = useAppSelector(
    (state) => state.deviceReducer.latestTagData
  );

  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  // Merge live data so deviceStatus/daysBehind are recomputed from latestTagData,

  const liveSelectedAsset = useMemo(() => {
    if (!selectedAsset) return selectedAsset;
    const [withLiveData] = addLiveDataToAssets([selectedAsset], latestTagData);
    return withLiveData;
  }, [selectedAsset, latestTagData]);
  const lastIngestedTime = useMemo(
    () =>
      Array.isArray(latestTagData)
        ? undefined
        : convertUtcToDisplayTime(
            latestTagData[selectedAsset?.device?.deviceId]?.ingestDate,
            preferredTimeDisplayMode,
            timezone,
            DATE_TIME_FORMAT
          ),
    [latestTagData]
  );

  return (
    <StyledBox sx={{ height: '100%' }}>
      <StyledTypography>
        {t('asset_details')} <SubText>{t('last_injested_time')}</SubText>{' '}
        <SubText style={{ fontWeight: '600' }}>{lastIngestedTime}</SubText>
      </StyledTypography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 2,
          boxSizing: 'border-box',
          maxHeight: '100%',
          alignItems: 'center',
          '& > *:not(:last-child)': {
            borderRight: `1px solid ${
              enableDarkTheme
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(0, 0, 0, 0.12)'
            }`,
            paddingRight: 2,
          },
        }}
      >
        <FlexBox sx={{ gridColumn: 'span 2' }}>
          {selectedAsset && (
            <PNG
              name={getImageName(selectedAsset, 'Small')}
              style={{
                height: '70px',
                width: '90px',
                marginLeft: '-20px',
                position: 'relative',
                zIndex: 1,
              }}
            />
          )}
          <Box>
            <StyledTypography fontSize={11} sx={{ fontWeight: 'normal' }}>
              {t('asset_id')}
            </StyledTypography>
            <FlexBox sx={{ alignItems: 'end' }}>
              <IgnitionStatusIndicator
                latestTagData={latestTagData}
                asset={liveSelectedAsset}
                size={10}
                marginRight="5px"
              />
              <StyledTypography fontSize={11} sx={{ fontWeight: '600' }}>
                {selectedAsset?.bumperNumber}
              </StyledTypography>
            </FlexBox>
          </Box>
        </FlexBox>
        <FlexBox sx={{ flexDirection: 'column' }}>
          <StyledTypography fontSize={11} sx={{ fontWeight: 'normal' }}>
            {t('loc_importer_col_type')}
          </StyledTypography>
          <StyledTypography fontSize={11} sx={{ fontWeight: '600' }}>
            {selectedAsset?.assetType
              ? t(
                  getAssetTypeLabelKey(
                    assetTypeMappings[selectedAsset.assetType]
                  )
                )
              : ''}
          </StyledTypography>
        </FlexBox>
        <FlexBox sx={{ flexDirection: 'column' }}>
          <StyledTypography
            fontSize={11}
            sx={{ fontWeight: 'normal', textAlign: 'center' }}
          >
            {t('hierarchy_node')}
          </StyledTypography>
          <StyledTypography fontSize={11} sx={{ fontWeight: '600' }}>
            {hierarchyInfo.nodeDisplay}
          </StyledTypography>
        </FlexBox>
        <FlexBox sx={{ flexDirection: 'column' }}>
          <StyledTypography fontSize={11} sx={{ fontWeight: 'normal' }}>
            OEM
          </StyledTypography>
          <StyledTypography fontSize={11} sx={{ fontWeight: '600' }}>
            {selectedAsset?.make ?? ''}
          </StyledTypography>
        </FlexBox>
        <FlexBox sx={{ flexDirection: 'column' }}>
          <StyledTypography fontSize={11} sx={{ fontWeight: 'normal' }}>
            {t('model_10')}
          </StyledTypography>
          <StyledTypography fontSize={11} sx={{ fontWeight: '600' }}>
            {selectedAsset?.model ?? ''}
          </StyledTypography>
        </FlexBox>
        <FlexBox sx={{ flexDirection: 'column' }}>
          <StyledTypography fontSize={11} sx={{ fontWeight: 'normal' }}>
            {t('status')}
          </StyledTypography>
          <StyledTypography fontSize={11} sx={{ fontWeight: '600' }}>
            {selectedAsset?.isActive
              ? t('operational_status')
              : t('out_of_service_status')}
          </StyledTypography>
        </FlexBox>
      </Box>
    </StyledBox>
  );
};

export default memo(AssetDetails);
